import { baseApi } from '@services/api/baseApi';
import {
  invalidateTagsOnSuccess,
  invalidateTagsOnSuccessConst,
  NO_RTK_CACHE,
} from '@services/api/rtkqTagHelpers';
import { type ListQuery, listQueryToParams, type ResponseDataList } from '@shared/api/list';

import { markerPositionToBbox } from './markerPosition';
import type {
  CreateDoorRequest,
  ManagedDoor,
  UpdateDoorMarkerPositionRequest,
  UpdateDoorRequest,
} from './types';

export type DoorListQuery = ListQuery & { evaluationId?: string };

export function evaluationDoorsListQuery(evaluationId: string): DoorListQuery {
  return {
    evaluationId,
    page: 1,
    limit: 500,
    sortBy: 'external_door_id',
    sortDir: 'asc',
  };
}

function applyMarkerPositionToCache(row: ManagedDoor, body: UpdateDoorMarkerPositionRequest): void {
  row.pageNumber = body.pageNumber;
  if (body.bboxX1 != null && body.bboxY1 != null && body.bboxX2 != null && body.bboxY2 != null) {
    row.bboxX1 = body.bboxX1;
    row.bboxY1 = body.bboxY1;
    row.bboxX2 = body.bboxX2;
    row.bboxY2 = body.bboxY2;
    return;
  }
  Object.assign(row, markerPositionToBbox(body.leftPct, body.topPct));
}

const doorsApiBase = baseApi.enhanceEndpoints({
  addTagTypes: ['Door', 'Evaluation', 'EvaluationPage'],
});

const doorMutationInvalidates = [
  { type: 'Door' as const, id: 'LIST' },
  { type: 'Evaluation' as const, id: 'LIST' },
  // The progress board's per-page door and needs-attention counts are computed server-side from
  // that page's doors, so any door write makes them stale. Invalidated by type rather than by
  // evaluation id because delete only carries the door id. Untagged by id = every entry.
  { type: 'EvaluationPage' as const },
] as const;

export const doorsApi = doorsApiBase.injectEndpoints({
  endpoints: (build) => ({
    listDoors: build.query<ResponseDataList<ManagedDoor>, DoorListQuery>({
      query: ({ evaluationId, ...q }) => ({
        url: 'doors',
        params: {
          ...listQueryToParams(q),
          ...(evaluationId ? { evaluation_id: evaluationId } : {}),
        },
      }),
      ...NO_RTK_CACHE,
      providesTags: () => [],
    }),
    createDoor: build.mutation<ManagedDoor, CreateDoorRequest>({
      query: (body) => ({ url: 'doors', method: 'POST', body }),
      invalidatesTags: invalidateTagsOnSuccessConst(doorMutationInvalidates),
    }),
    updateDoor: build.mutation<unknown, { id: string; body: UpdateDoorRequest }>({
      query: ({ id, body }) => ({ url: `doors/${id}`, method: 'PUT', body }),
      invalidatesTags: invalidateTagsOnSuccess((_result, arg) => [
        { type: 'Door', id: arg.id },
        ...doorMutationInvalidates,
      ]),
    }),

    updateDoorMarker: build.mutation<
      unknown,
      { id: string; body: UpdateDoorMarkerPositionRequest; listQuery: DoorListQuery }
    >({
      query: ({ id, body }) => ({
        url: `doors/${id}/marker-position`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: () => [],
      async onQueryStarted({ id, body, listQuery }, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          doorsApi.util.updateQueryData('listDoors', listQuery, (draft) => {
            const row = draft.data.find((d) => d.id === id);
            if (row) applyMarkerPositionToCache(row, body);
          }),
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
    }),
    deleteDoor: build.mutation<unknown, string>({
      query: (id) => ({ url: `doors/${id}`, method: 'DELETE' }),
      invalidatesTags: invalidateTagsOnSuccess((_result, id) => [
        { type: 'Door', id },
        ...doorMutationInvalidates,
      ]),
    }),
  }),
});

export const {
  useListDoorsQuery,
  useCreateDoorMutation,
  useUpdateDoorMutation,
  useUpdateDoorMarkerMutation,
  useDeleteDoorMutation,
} = doorsApi;
